import {
  FSMBehaviorBase,
  FSMActionSpec,
  FSMState,
  FSMStateSlotDefinition,
  FSMStateType,
  FSMTransition,
  FSMVariableSpec,
  FSMVariableTypeString,
  NODE_STATE_TYPE_MAP,
  PREAMBLE,
  STATE_TRIGGER_MAP,
  mapOperatorToSymbol,
  mapStringOperatorToWord,
} from "@/lib/constants";
import {Variable, getVariableParams} from "@/contexts/VariablesContext";
import {
  Condition,
  FlowNodeTypes,
  ProcessedNode,
  VariableTypes,
} from "@/types/node";
import {
  TransferCallAction,
  BookingAction,
} from "@/components/settings/Actions/SelectActionsTable";
import {Edge} from "@xyflow/react";
import {CustomAction} from "@/api/services/customAction";
import {VariableType} from "@/components/settings/constants";

// FSM Utils
const populateVariables = (instructions: string): string => {
  if (!instructions) return "";

  const variableParams = getVariableParams();
  let output = instructions;

  // Handle angle bracket placeholders from RESULTS: <...>
  const resultsParams = variableParams[VariableTypes.RESULTS];
  if (resultsParams) {
    output = output.replace(/<([^<>]+)>/g, (_m, inner: string) => {
      const sanitized = inner.replace(/[:.]/g, "_");
      return `{{${sanitized}}}`;
    });
  }

  // Handle single-curly placeholders from VARIABLES: {...} -> {{...}}
  const varsParams = variableParams[VariableTypes.VARIABLES];
  if (varsParams && varsParams.open === "{" && varsParams.close === "}") {
    const OPEN_GUARD = "__DC_OPEN__";
    const CLOSE_GUARD = "__DC_CLOSE__";
    output = output
      .replace(
        /\{\{([^{}]+)\}\}/g,
        (_m, inner: string) => `${OPEN_GUARD}${inner}${CLOSE_GUARD}`,
      )
      .replace(/\{([^{}]+)\}/g, (_m, inner: string) => `{{${inner}}}`)
      .replace(
        new RegExp(`${OPEN_GUARD}([^]+?)${CLOSE_GUARD}`, "g"),
        (_m, inner: string) => `{{${inner}}}`,
      );
  }

  return output;
};

export const serializeResultVariables = (instructions: string): string => {
  if (!instructions) return instructions;
  return instructions
    .replace(/<([^<>:]+):([^<>:]+)>/g, "{$2}")
    .split(".")
    .join("_");
};

export const extractPreambleBody = (globalNode: ProcessedNode): string => {
  const globalInstructions = serializeResultVariables(
    globalNode.data.settings?.global_instructions || "",
  );
  const objections = globalNode.data.settings?.special_cases || [];

  const backgroundInfo = `BACKGROUND INFORMATION\n${globalInstructions}`;
  const objectionHandling =
    "Objection Handling\n" +
    objections
      .map(item => `“${item.title}”\n“${item.description}”`)
      .join("\n\n");

  return [backgroundInfo, objectionHandling].join("\n\n");
};

export const makePreamble = (globalNode: ProcessedNode | null): string => {
  if (!globalNode) return "";

  const {header, footer} = PREAMBLE;

  const body = extractPreambleBody(globalNode);

  const preamble = [header, body, footer]
    .map(section => section.replace(/\u200B/g, ""))
    .join("\n\n");

  return preamble;
};

export const formatVariables = (
  variables: Variable[] | string[],
): Record<string, FSMVariableSpec> => {
  if (!Array.isArray(variables) || variables.length === 0) return {};

  const first = variables[0] as any;
  const isStringInput = typeof first === "string";

  if (isStringInput) {
    return Object.fromEntries(
      (variables as string[]).map(v => [
        v.split(".").join("_") || "",
        {type: "string" as const},
      ]),
    );
  }

  const allowed: ReadonlySet<FSMVariableTypeString> = new Set([
    "string",
    "boolean",
    "number",
    "integer",
    "enum",
  ]);

  return Object.fromEntries(
    (variables as Variable[]).map(v => {
      const safeType = allowed.has(v.type as FSMVariableTypeString)
        ? (v.type as FSMVariableSpec["type"])
        : ("string" as const);

      const isEnum = v.type === VariableType.ENUM;
      const enumValue = isEnum ? v.enumOptions : null;

      return [v.name, {type: safeType, ...(enumValue && {values: enumValue})}];
    }),
  );
};

export const getNodesByType = (
  nodes: ProcessedNode[],
): Record<FlowNodeTypes, ProcessedNode[]> => {
  return nodes.reduce(
    (acc, node) => {
      if (node.type === FlowNodeTypes.PLACEHOLDER) return acc;

      const key = node.data.type as FlowNodeTypes;
      acc[key] = acc[key] || [];
      acc[key].push(node as unknown as ProcessedNode);
      return acc;
    },
    {} as Record<FlowNodeTypes, ProcessedNode[]>,
  );
};

export const getActionResults = (actionNodes: ProcessedNode[]): string[] => {
  return actionNodes.reduce(
    (acc, node) => [
      ...acc,
      ...(node.data.settings.action?.variables?.map(variable => {
        const name = node.data.settings.action?.name;
        if (!name) return variable;
        return `${name}.${variable}`;
      }) || []),
    ],
    [] as string[],
  );
};

export const getActionsByType = (
  nodes: ProcessedNode[],
): Record<FlowNodeTypes, ProcessedNode[]> => {
  const allTypes = Object.values(FlowNodeTypes) as FlowNodeTypes[];
  const initial: Record<FlowNodeTypes, ProcessedNode[]> = allTypes.reduce(
    (acc, t) => {
      acc[t] = [];
      return acc;
    },
    {} as Record<FlowNodeTypes, ProcessedNode[]>,
  );

  return nodes.reduce((acc, node) => {
    const key = node.data.type as FlowNodeTypes;
    acc[key].push(node as unknown as ProcessedNode);
    return acc;
  }, initial);
};

// FSM State Utils
const cleanResultVariableBrackets = (
  obj: Record<string, string> | null | undefined,
): Record<string, string> | null => {
  if (!obj) {
    return null;
  }
  const cleaned = Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, v.replace(/^<([^<>]+)>$/, "$1")]),
  );
  return Object.keys(cleaned).length === 0 ? null : cleaned;
};

const getReturnMappings = (actionResults: string[]): Record<string, string> => {
  if (!actionResults || actionResults.length === 0) return null;
  return actionResults.reduce(
    (acc, r) => {
      acc[r.substring(r.indexOf(".") + 1)] = r.split(".").join("_");
      return acc;
    },
    {} as Record<string, string>,
  );
};

const getCondition = (node: ProcessedNode): string => {
  const deterministicConditions =
    node?.data.settings?.condition_type?.deterministic;
  const conditionRules = deterministicConditions?.rules as Condition[];
  const isAndConditions = deterministicConditions?.is_and;

  if (!conditionRules) return null;

  const flatConditions = conditionRules.reduce(
    (acc: string[], curr: Condition) => {
      const variable = curr.variable?.name;
      const variableType = curr.variable?.type;
      const operator = curr.operator;
      const value = curr.value;

      if (variable && operator && value) {
        const isStringLike = variableType === VariableType.STRING;
        const isBoolean = variableType === VariableType.BOOLEAN;
        const isEnum = variableType === VariableType.ENUM;

        if (isStringLike) {
          const opWord = mapStringOperatorToWord(operator);
          acc.push(`lower(${variable}) ${opWord} '${value}'`);
        } else if (isEnum) {
          const opSymbol = mapOperatorToSymbol(operator);
          acc.push(`${variable} ${opSymbol} '${value}'`);
        } else {
          const opSymbol = mapOperatorToSymbol(operator);
          const formattedValue = isBoolean
            ? String(value).toLowerCase()
            : value;
          acc.push(`${variable} ${opSymbol} ${formattedValue}`);
        }
      }
      return acc;
    },
    [],
  );

  return flatConditions.join(isAndConditions ? " && " : " || ");
};

const getTarget = (
  node: ProcessedNode,
  edges: Edge[],
  nodes: ProcessedNode[],
): string => {
  if (node.data.type === FlowNodeTypes.JUMP) {
    const jumpToStepValue = node.data.settings.jump_to_step;
    // Parse jump_to_step value (format: "subflowId:nodeId" or just "nodeId" for backward compatibility)
    // For now, we'll keep the full value as FSM might need to handle subflow transitions
    return jumpToStepValue;
  }

  if (node.data.type === FlowNodeTypes.BRANCH) {
    const outgoingBranchEdges = edges.filter(edge => edge.source === node.id);
    const branchTargetNode = nodes.find(
      node => node.id === outgoingBranchEdges[0].target,
    );
    if (!branchTargetNode) return null;

    if (branchTargetNode.data.type === FlowNodeTypes.JUMP) {
      const jumpToStepValue = branchTargetNode.data.settings.jump_to_step;
      // Parse jump_to_step value (format: "subflowId:nodeId" or just "nodeId" for backward compatibility)
      // For now, we'll keep the full value as FSM might need to handle subflow transitions
      return jumpToStepValue;
    }

    return branchTargetNode.id;
  }

  return node.id;
};

const getStateType = (node: ProcessedNode): FSMStateType => {
  return NODE_STATE_TYPE_MAP[node.data.type];
};

const getBehavior = (node: ProcessedNode): FSMBehaviorBase => {
  const type = getStateType(node);

  // Collect Variables Node Behavior
  if (type === FSMStateType.SLOT_COLLECTION_STATE) {
    const instructions = populateVariables(node.data.settings.instructions);

    const slotBehavior: FSMBehaviorBase = {
      slots_to_fill: node.data.settings.variables.map(v => v.name),
    };
    if (instructions) slotBehavior.instructions = instructions;

    return slotBehavior;
  }

  // Speak Node Behavior
  if (
    type === FSMStateType.UTTERANCE_STATE &&
    node.data.type == FlowNodeTypes.SPEAK
  ) {
    const instructions = populateVariables(node.data.settings.instructions);
    const verbatimSetting = node.data.settings.verbatim;

    const verbatim = verbatimSetting?.exact_message?.selected === true;

    return {
      verbatim: verbatim,
      instructions: instructions,
    };
  }

  // Transfer Node Behavior
  if (type === FSMStateType.TRANSFER_STATE) {
    const action = node.data.settings.action as TransferCallAction;
    if (!action) return null;
    return {
      transfer_id: action.action_id,
      transfer_type: action.call_transfer_info?.mode || "cold_transfer",
      context_message: action.trigger,
    };
  }

  // Action Node Behavior
  if (type === FSMStateType.ACTION_STATE) {
    const action = node.data.settings.action as CustomAction;
    if (!action) return null;
    const actionProperties: Record<string, string> = {};
    for (const key in action.body) {
      if (Object.prototype.hasOwnProperty.call(action.body, key)) {
        actionProperties[key] = String(
          action.body[key as keyof typeof action.body],
        );
      }
    }
    const actionResults = getActionResults([node]);

    const parameterMappings = cleanResultVariableBrackets(actionProperties);
    const returnMappings = getReturnMappings(actionResults);

    const actionSpec: FSMActionSpec = {
      action_id: action.action_id,
      name: action.name,
      description: action.description,
    };

    if (parameterMappings) actionSpec.parameter_mappings = parameterMappings;
    if (returnMappings) actionSpec.return_mappings = returnMappings;

    return {
      actions: [actionSpec],
    };
  }

  // Default Node Behavior
  return {
    instructions: populateVariables(
      node.data.settings.instructions ||
        node.data.settings.scripted_greeting ||
        "",
    ),
  };
};

const getSlotDefinitions = (
  node: ProcessedNode,
): Record<string, FSMStateSlotDefinition> => {
  const variables = node.data.settings.variables;

  if (!variables || variables.length === 0) {
    return null;
  }
  // Convert variables to slot definitions format
  const slotDefinitions = {};

  variables.forEach(variable => {
    slotDefinitions[variable.name] = {
      type: variable.type,
      prompt: variable.instructions,
    };

    if (variable.required) {
      slotDefinitions[variable.name].validation_rules = ["required"];
    }
  });

  return slotDefinitions;
};

const getTransitions = (
  node: ProcessedNode,
  edges: Edge[],
  nodes: ProcessedNode[],
): FSMTransition[] => {
  const targetNodes = edges
    .filter(
      edge =>
        edge.source === node.id && node.data.type !== FlowNodeTypes.PLACEHOLDER,
    )
    .map(edge => nodes.find(node => node.id === edge.target))
    .filter(Boolean);

  const transitions: FSMTransition[] = [];

  targetNodes.forEach(targetNode => {
    const hasVariables = node.data.settings.variables?.length > 0;
    const trigger = hasVariables
      ? "slots_completed"
      : STATE_TRIGGER_MAP[node.type];

    const condition = getCondition(targetNode);
    const promptCondition =
      targetNode.data.settings.condition_type?.prompt?.description;

    const target = getTarget(targetNode, edges, nodes);
    transitions.push({
      trigger,
      target,
    });

    if (condition) transitions[transitions.length - 1].condition = condition;
    if (promptCondition)
      transitions[transitions.length - 1].prompt_condition = promptCondition;
  });

  return transitions;
};

const getState = (
  node: ProcessedNode,
  edges: Edge[],
  nodes: ProcessedNode[],
): FSMState => {
  const type = getStateType(node);
  if (!type) return null;

  const behavior = getBehavior(node);
  const slot_definitions = getSlotDefinitions(node);
  const transitions = getTransitions(node, edges, nodes);

  const state: FSMState = {
    type,
    behavior,
  };

  if (slot_definitions) state.slot_definitions = slot_definitions;
  if (transitions) state.transitions = transitions;

  return state;
};

export const getStatesFromNodes = (
  nodes: ProcessedNode[],
  edges: Edge[],
): Record<string, FSMState> => {
  return nodes.reduce(
    (acc, node) => {
      if (node.type === FlowNodeTypes.BOOKING) {
        const bookingStates = buildBookingStates(
          node as ProcessedNode,
          edges,
          nodes,
        );
        Object.entries(bookingStates).forEach(([id, st]) => {
          acc[id] = st;
        });
        return acc;
      }
      const state = getState(node, edges, nodes);
      if (!state) return acc;
      acc[node.id] = state;
      return acc;
    },
    {} as Record<string, FSMState>,
  );
};

// Booking Node Expansion
const buildBookingStates = (
  node: ProcessedNode,
  edges: Edge[],
  nodes: ProcessedNode[],
): Record<string, FSMState> => {
  const states: Record<string, FSMState> = {};

  const entryId = node.id; // ask_for_slot_and_timezone should be entry (previous nodes point here)
  const availabilityId = `${node.id}_avail`;
  const createId = `${node.id}_create`;
  const availabilityFailedId = `${node.id}_avail_failed`;
  const bookingFailedId = `${node.id}_booking_failed`;

  // Unique variable names for this booking node
  const v = (suffix: string) => `${node.id}_${suffix}`;
  const lead_timezone = v("lead_timezone");
  const availability_start = v("availability_start");
  const availability_end = v("availability_end");
  const timezone = v("timezone");
  const requested_slot_start = v("requested_slot_start");
  const booking_status = v("booking_status");
  const booking_error = v("booking_error");
  const user_rejected_availabilities = v("user_rejected_availabilities");
  const available_slots = v("available_slots");

  // Booking action details (provider, id)
  const bookingAction = (node.data.settings.action || {}) as BookingAction;
  const provider = bookingAction?.config?.type;

  const availabilityActionName =
    provider === "ghl"
      ? "action_ghl_get_availability"
      : "action_calcom_get_availability";
  const createBookingActionName =
    provider === "ghl"
      ? "action_ghl_create_booking"
      : "action_calcom_create_booking";

  // Determine external next state after booking node (first outgoing edge target processed via getTarget)
  const nextEdge = edges.find(e => e.source === node.id);
  let externalNextTarget: string = null;
  if (nextEdge) {
    const nextNode = nodes.find(n => n.id === nextEdge.target);
    if (nextNode) {
      externalNextTarget = getTarget(nextNode, edges, nodes);
    }
  }

  // get_secondary_availability (action_state)
  states[availabilityId] = {
    type: FSMStateType.ACTION_STATE,
    behavior: {
      instructions: `In this state you will call the availability action in order to fetch the availabilities. By default, check the availabilities for the next day, but prefer the date that the user requests in the conversation. The results will then be set in the variable ${requested_slot_start}. An example availability action call may look like this: 'execute_action ${availabilityActionName} {"start":"2025-08-29T15:00:00","end":"2025-08-30T23:59:59","timezone":"America/New_York"}' or another example 'execute_action ${availabilityActionName} {"start":"2025-06-19T09:30:00","end":"2025-06-21T23:59:59","timezone":"Europe/Berlin"}'`,
      actions: [
        {
          name: availabilityActionName,
          description: "Fetch available slots",
          parameter_mappings: {
            timezone: lead_timezone,
            start: availability_start,
            end: availability_end,
          },
          return_mappings: {
            slots: available_slots,
          },
        },
      ],
    },
    transitions: [
      {
        trigger: "action_success",
        target: entryId,
        clear_on_enter: [user_rejected_availabilities, requested_slot_start],
      },
      {trigger: "action_failure", target: availabilityFailedId},
    ],
  };

  // ask_for_slot_and_timezone (slot_collection_state) — entryId
  states[entryId] = {
    type: FSMStateType.SLOT_COLLECTION_STATE,
    behavior: {
      instructions: `Your job in this state is to find a time slot that suits the user for the booking. Use the variable ${available_slots} and propose the slots to the user for them to choose. If the user requests available slots or is unhappy with the selection, set ${user_rejected_availabilities} to 'true' and the state machine will transition to the next state to fetch availabilities. YOU MUST ONLY SET SLOTS IN THIS STATE.`,
      slots_to_fill: [
        requested_slot_start,
        timezone,
        user_rejected_availabilities,
      ],
    },
    slot_definitions: {
      [requested_slot_start]: {
        type: "string",
        prompt:
          "The start time of a slot that the user requests. Only set this if the user specifically requests the time slot. Use an ISO 8601 date format like: %Y-%m-%dT%H:%M:%S for example: 2025-08-29T15:00:00.",
        validation_rules: ["required"],
      },
      [timezone]: {
        type: "string",
        prompt:
          "The user's timezone in a standardized format like Europe/Berlin.",
        validation_rules: ["required"],
      },
      [user_rejected_availabilities]: {
        type: "boolean",
        prompt: `If the user expresses that none of the available time slots suit them or if they would like you to propose available slots set ${user_rejected_availabilities} to 'true' so you can transition into the next state to fetch new availabilities.`,
      },
    },
    transitions: [
      // If user rejected availability, loop back to availability fetch
      {
        trigger: "slot_completed",
        target: availabilityId,
        condition: `${user_rejected_availabilities}==true`,
      },
      // Otherwise proceed to create booking
      {trigger: "slots_completed", target: createId},
    ],
  };

  // create_booking (action_state)
  states[createId] = {
    type: FSMStateType.ACTION_STATE,
    behavior: {
      instructions: `In this state you will call an action to create the booking with the selected time and timezone. IMPORTANT: The format of the date should be ISO 8601 date format like: %Y-%m-%dT%H:%M:%S for example: 2025-08-29T15:00:00. The timezone should be in the format Europe/Berlin. Make sure to set the value based on what the user has requested and saved in the ${requested_slot_start} variable. Here is an example create booking action: 'execute_action ${createBookingActionName} {"start":"2025-09-19T09:00:00","timezone":"Europe/Berlin"}' and another example: 'execute_action ${createBookingActionName} {"start":"2025-08-29T02:00:00","timezone":"America/New_York"}'`,
      actions: [
        {
          action_id: bookingAction?.action_id,
          name: createBookingActionName,
          description: "Create booking",
          parameter_mappings: {
            start: requested_slot_start,
            timezone: timezone,
          },
          return_mappings: {
            status: booking_status,
            error_message: booking_error,
          },
        },
      ],
    },
    transitions: [
      {trigger: "action_success", target: externalNextTarget || entryId},
      {trigger: "action_failure", target: bookingFailedId},
    ],
  };

  // availability_failed (utterance_state)
  states[availabilityFailedId] = {
    type: FSMStateType.UTTERANCE_STATE,
    behavior: {
      instructions:
        "I couldn’t check the available slots. Let me try pulling availability again.",
    },
    transitions: [{trigger: "user_response", target: availabilityId}],
  };

  // booking_failed (utterance_state)
  states[bookingFailedId] = {
    type: FSMStateType.UTTERANCE_STATE,
    behavior: {
      instructions:
        "I couldn’t create the booking. Let's try a different time or timezone.",
    },
    transitions: [{trigger: "user_response", target: entryId}],
  };

  return states;
};

// Collect booking-specific variables for FSM.variables
export const getBookingVariables = (
  nodes: ProcessedNode[],
): Record<string, FSMVariableSpec> => {
  const result: Record<string, FSMVariableSpec> = {};
  nodes.forEach(n => {
    if (n.type !== FlowNodeTypes.BOOKING) return;
    const v = (suffix: string) => `${n.id}_${suffix}`;
    const add = (name: string, type: FSMVariableTypeString) => {
      result[name] = {type};
    };
    add(v("lead_timezone"), "string");
    add(v("availability_start"), "string");
    add(v("availability_end"), "string");
    add(v("lead_name"), "string");
    add(v("timezone"), "string");
    add(v("requested_slot_start"), "string");
    add(v("booking_status"), "string");
    add(v("booking_error"), "string");
    add(v("user_rejected_availabilities"), "boolean");
    add(v("available_slots"), "array");
  });
  return result;
};
